import * as React from 'react';
import { Component } from 'react';
import { observer } from 'mobx-react';
import { inject } from '../utils';
import { action, observable, computed } from 'mobx';
import { withTheme } from 'styled-components';
import { styled } from '../utils/styled';
import { propStyles } from '../utils/propStyles';
import { top } from '../utils/customPropStyles';
import SortToggle from './SortToggle';
import getSortKeyFromColumn from '../utils/getSortKeyFromColumn';
import isExistingAdvancedSort from '../utils/isExistingAdvancedSort';
import get from 'lodash/get';
import { DataStore, AccessibilityStore } from '../stores';
import Dropdown from './Dropdown';
import { DropdownThemeKeys } from './Dropdown/Models';
import { SORT_SELECTOR_PLACEHOLDER } from '../constants';
import { ColumnConfig } from '../models/configuration/explorer/ColumnConfig';
import getColumnLabel from '../utils/getColumnLabel';
import { Theme } from '../models/Theme';

const SortSelectorWrapper = styled<any, any>(
    'div',
    'sortSelectorWrapper',
    'sortSelector'
)`
    ${propStyles};
`;

SortSelectorWrapper.defaultProps = {
    flex: '1 0 100%',
    paddingY: 'sm',
    bgColor: 'white',
};

const SortSelectorInnerWrapper = styled<any, any>(
    'div',
    'sortSelectorInnerWrapper',
    'sortSelector'
)`
    ${propStyles};
`;

SortSelectorInnerWrapper.defaultProps = {
    position: 'relative',
    display: 'flex',
};

const SortSelectorDropdownButton = styled<any, any>(
    'button',
    'sortSelectorDropdownButton',
    'sortSelector'
)`
    ${propStyles};
    outline: none;
    cursor: pointer;
`;

SortSelectorDropdownButton.defaultProps = {
    display: 'flex',
    flex: '1 1 auto',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    overflow: 'hidden',
    padding: 'sm',
    fontSize: 'sm',
    fontFamily: 'body',
    fontWeight: 'normal',
    color: 'body',
    bgColor: 'white',
    borderWidth: '1px',
    borderColor: 'gray300',
    borderStyle: 'solid',
    borderRadius: 'sm',
    hover: {
        borderColor: 'gray500',
    },
    selected: {
        color: 'body',
        borderColor: 'gray500',
    },
};

const SortSelectorDropdownText = styled<any, any>(
    'div',
    'sortSelectorDropdownText',
    'sortSelector'
)`
    ${propStyles};
    white-space: nowrap;
`;

SortSelectorDropdownText.defaultProps = {
    textAlign: 'left',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    color: 'inherit',
};

const SortSelectorDropdownMenu = styled<any, any>(
    'div',
    'sortSelectorDropdownMenu',
    'sortSelector'
)`
    ${propStyles};
    ${top};
`;

SortSelectorDropdownMenu.defaultProps = {
    position: 'absolute',
    top: 'calc(100% + 1px)',
    zIndex: 'dropdown',
    width: ['100%', '80%'],
    margin: ['0', '0 10%'],
    bgColor: 'white',
    borderWidth: '1px',
    borderColor: 'gray300',
    borderStyle: 'solid',
    borderRadius: 'sm',
    boxShadow: 'sm',
};

const SortSelectorDropdownList = styled<any, any>(
    'ol',
    'sortSelectorDropdownList',
    'sortSelector'
)`
    ${propStyles};
    list-style: none;
    -ms-overflow-style: -ms-autohiding-scrollbar;
`;

SortSelectorDropdownList.defaultProps = {
    maxHeight: '300px',
    overflowX: 'hidden',
    overflowY: 'auto',
};

const SortSelectorDropdownListItem = styled<any, any>(
    'li',
    'sortSelectorDropdownListItem',
    'sortSelector'
)`
    ${propStyles};
`;

SortSelectorDropdownListItem.defaultProps = {
    fontFamily: 'body',
    fontWeight: 'normal',
    paddingY: 'sm',
    paddingX: 'md',
    borderWidth: '1px',
    borderStyle: 'none none solid none',
    borderColor: 'gray300',
    cursor: 'pointer',
    alternated: {
        bgColor: 'gray100',
    },
    hover: {
        bgColor: 'gray100',
    },
    selected: {
        color: 'primary',
        fontWeight: 'bold',
    },
};

interface SortSelectorProps {
    theme?: Theme;
    dataStore?: DataStore;
    order?: string;
    accessibilityStore?: AccessibilityStore;
}

@inject('dataStore', 'accessibilityStore')
@observer
class SortSelector extends Component<SortSelectorProps> {
    @observable isOpen = false;
    @observable.ref selectedColumn: ColumnConfig;

    constructor(props) {
        super(props);
        this.initialize();
    }

    initialize() {
        if (this.sortableColumns && this.sortableColumns[0]) {
            this.selectedColumn = this.sortableColumns[0];
        }
    }

    @computed
    get sortableColumns() {
        const { dataStore } = this.props;
        return dataStore?.columnStore?.sortableColumns;
    }

    @action
    toggleOpen = () => {
        this.isOpen = !this.isOpen;
    };

    @action
    selectSortColumn = column => {
        this.selectedColumn = column;
        this.props.dataStore.toggleSort(column);
    };

    @computed
    get direction() {
        let direction;

        if (this.selectedColumn) {
            const key = getSortKeyFromColumn(this.selectedColumn);

            if (
                this.props.dataStore.advancedSort &&
                isExistingAdvancedSort(
                    this.selectedColumn,
                    this.props.dataStore.advancedSort
                )
            ) {
                direction = get(
                    this.props.dataStore,
                    'advancedSort.direction',
                    ''
                );
            } else {
                direction = get(
                    this.props.dataStore.propertySort.find(c => c.key === key),
                    'direction',
                    ''
                );
            }
        }

        return direction;
    }

    @computed
    get dropdownThemeKeys(): DropdownThemeKeys {
        return {
            button: 'SortSelectorDropdownButton',
            buttonIcon: 'SortSelectorDropdownChevron',
            buttonLabel: 'SortSelectorDropdownText',
            list: 'SortSelectorDropdownList',
            listItem: 'SortSelectorDropdownListItem',
            listWrapper: 'SortSelectorDropdownMenu',
            root: 'SortSelectorWrapper',
            wrapper: 'SortSelectorInnerWrapper',
            disclaimerWrapper: 'SortSelectorDisclaimerWrapper',
        };
    }

    render() {
        const { dataStore, theme } = this.props;
        return (
            <Dropdown
                items={dataStore.columnStore.sortableColumns}
                itemLabelFn={item => getColumnLabel(item, dataStore)}
                selectItemFn={this.selectSortColumn}
                selectedItem={this.selectedColumn}
                placeHolder={SORT_SELECTOR_PLACEHOLDER}
                chevronIcon={'SortSelectorDropdownChevronIcon.svg'}
                chevronIconExpanded={
                    'SortSelectorDropdownChevronIconExpanded.svg'
                }
                themeKeys={this.dropdownThemeKeys}
            >
                <SortToggle
                    direction={this.direction}
                    onClick={() => dataStore.toggleSort(this.selectedColumn)}
                    disabled={!this.selectedColumn}
                    paddingY="sm"
                    paddingX="md"
                    margin="0 0 0 10px"
                    color="gray500"
                    bgColor="gray100"
                    borderWidth="1px"
                    borderColor="gray300"
                    borderStyle="solid"
                    borderRadius="sm"
                    boxShadow="sm"
                    hover={{ borderColor: 'gray500' }}
                    metricContext={'mobile'}
                    {...theme.SortSelectorSortToggle}
                />
            </Dropdown>
        );
    }
}

export default withTheme(SortSelector);



// WEBPACK FOOTER //
// ./src/Components/SortSelector.tsx